import { useEffect, useState } from 'react';
import { AnalyticsWebClient, CommunityAnalyticsClient, CookieCompliance } from '@atlassian/community-library';
import { ENVIRONMENT, getEnvironment } from '../utils/environment';

interface Props {
  userId: string;
}

// Use the shared OneTrust gate from community-library so we do not
// have to re-implement polling/timeout here. If OneTrust never publishes a
// value, the helper resolves after maxWaitMs and the library's
// CookieCompliance._noPerformanceCookieConsent() falls through to its
// fail-closed branch (no signal -> treat as opted-out -> only the strictly
// necessary requested_page operational event fires).
const ONE_TRUST_MAX_WAIT_MS = 3000;

export const useInitializeAnalyticsClient = ({ userId }: Props) => {
  const [analyticsClient, setAnalyticsClient] = useState<AnalyticsWebClient | undefined>(undefined);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      // Wait for OneTrust to publish OnetrustActiveGroups before
      // initialising the wrapper, so CookieCompliance has a real signal to
      // read when it gates non-operational events at fire time.
      // Feature-detected so this consumer keeps working against older library
      // versions that ship without the helper (waitForOneTrustToInit was added
      // in 2.4.x). When unavailable, we still proceed -- the library's per-event
      // optOutPerfTracking() check will gate non-operational events on its own.
      const cookieCompliance = CookieCompliance as unknown as
        { waitForOneTrustToInit?: (maxWaitMs?: number) => Promise<void> };
      if (typeof cookieCompliance?.waitForOneTrustToInit === 'function') {
        await cookieCompliance.waitForOneTrustToInit(ONE_TRUST_MAX_WAIT_MS);
      }
      if (cancelled) {
        return;
      }
      const client = CommunityAnalyticsClient.initWithPageAnalytics(
        CommunityAnalyticsClient.SUB_APP.HOME,
        null,
        getEnvironment() as ENVIRONMENT,
        {},
        // Pass null for explicit-anonymous so the library skips
        // the legacy identity-cookie hydration and clearUserInfo()s any
        // stale AAID. Authenticated sessions pass the AAID through as-is.
        userId || null,
      );

      setAnalyticsClient(client);
    })();
    return () => {
      cancelled = true;
    };
  }, [userId]);

  return { analyticsClient };
};
